class CartProgressBar extends HTMLElement {
  static debugEnabled(el) {
    if (el?.dataset?.debug === 'true') return true;
    try {
      return new URLSearchParams(window.location.search).get('cart_progress_debug') === '1';
    } catch (e) {
      return false;
    }
  }

  static logDebug(el, payload) {
    const line = `[CartProgressBar] ${JSON.stringify(payload)}`;
    console.log(line);
    if (!CartProgressBar.debugEnabled(el)) return;
    let pre = el.querySelector('.cart-progress-bar__debug');
    if (!pre) {
      pre = document.createElement('pre');
      pre.className = 'cart-progress-bar__debug';
      pre.setAttribute('aria-hidden', 'true');
      el.appendChild(pre);
    }
    pre.textContent = `[CartProgressBar JS]\n${Object.entries(payload)
      .map(([k, v]) => `${k}: ${v}`)
      .join('\n')}`;
  }

  constructor() {
    super();
  }

  connectedCallback() {
    const rawThreshold = String(this.dataset.thresholdCents || this.dataset.threshold || '').trim();
    this.threshold = parseInt(rawThreshold, 10);
    if (!Number.isFinite(this.threshold) || this.threshold <= 0) {
      this.threshold = 0;
    }

    this.progressMessage = this.dataset.progressMessage || '';
    this.successMessage = this.dataset.successMessage || '';
    this.currency = this.dataset.currency || 'USD';

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      this.unsub = subscribe(PUB_SUB_EVENTS.cartUpdate, (e) => e.source !== 'cart-progress-bar' && this.fetchCart());
    }

    this.fetchCart();
  }

  disconnectedCallback() {
    this.unsub && this.unsub();
  }

  fetchCart() {
    const cartUrl = typeof routes !== 'undefined' && routes.cart_url ? `${routes.cart_url}.js` : '/cart.js';
    fetch(cartUrl)
      .then((r) => r.json())
      .then((cart) => this.update(cart))
      .catch(console.error);
  }

  eligibleTotalCents(cart) {
    let total = typeof cart.total_price === 'number' ? cart.total_price : 0;
    (cart.items || []).forEach((i) => {
      const itemPrice = i.final_line_price !== undefined ? i.final_line_price : i.line_price || 0;
      if (i.requires_shipping === false) total -= itemPrice;
    });
    return Math.max(0, Math.round(total));
  }

  computeProgressPct(totalCents, thresholdCents) {
    if (!thresholdCents || thresholdCents <= 0) return 0;
    const pct = (totalCents / thresholdCents) * 100;
    return Math.min(100, Math.max(0, pct));
  }

  update(cart) {
    if (!cart) return;

    const items = cart.items || [];
    let shippableLines = 0;
    items.forEach((i) => {
      if (i.requires_shipping !== false) shippableLines += 1;
    });

    const show = items.length > 0 && shippableLines > 0 && this.threshold > 0;
    const sec = this.closest('.cart-progress-bar-sec');

    this.hidden = !show;
    if (sec) sec.classList.toggle('cart-progress-bar-sec--hidden', !show);
    this.style.display = show ? 'flex' : 'none';

    if (!show) {
      CartProgressBar.logDebug(this, { show: false, item_count: items.length, shippable_lines: shippableLines, threshold_cents: this.threshold });
      return;
    }

    const total = this.eligibleTotalCents(cart);
    const threshold = this.threshold > 0 ? this.threshold : 0;
    const pct = this.computeProgressPct(total, threshold || 1);
    const success = threshold > 0 && total >= threshold;
    const remaining = threshold > 0 ? Math.max(0, threshold - total) : 0;
    const widthPct = success ? 100 : pct;

    const msgEl = this.querySelector('.cart-progress-bar__message');
    const progressEl = this.querySelector('.cart-progress-bar__progress');

    if (msgEl) {
      const amount = this.formatMoney(remaining);
      msgEl.innerHTML = success
        ? this.successMessage
        : this.progressMessage.replace(/\{\{\s*amount\s*\}\}/g, amount);
    }

    if (progressEl) {
      const widthStr = widthPct.toFixed(2);
      progressEl.style.width = `${widthStr}%`;
      progressEl.setAttribute('aria-valuenow', widthStr);
    }

    this.dataset.eligibleTotalCents = String(total);
    this.dataset.progressPct = widthPct.toFixed(2);

    this.classList.toggle('is-success', success);

    CartProgressBar.logDebug(this, {
      threshold_cents: threshold,
      cart_total_price: cart.total_price,
      eligible_total_cents: total,
      remaining_cents: remaining,
      progress_pct: widthPct.toFixed(2),
      bar_width_pct: widthPct.toFixed(2),
      is_success: success,
      currency: cart.currency || this.currency,
    });
  }

  formatMoney(cents) {
    const centsInt = Math.round(Number(cents) || 0);
    const moneyFormat =
      (typeof window.theme !== 'undefined' && window.theme.moneyFormat) ||
      (typeof Shopify !== 'undefined' && Shopify.money_format) ||
      '${' + '{{amount}}';
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(centsInt, moneyFormat);
    }
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: this.currency || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(centsInt / 100);
  }
}

if (!customElements.get('cart-progress-bar')) {
  customElements.define('cart-progress-bar', CartProgressBar);
}

window.refreshCartProgressBar = function () {
  document.querySelector('cart-progress-bar')?.fetchCart?.();
};

window.syncCartProgressBarFromDoc = function (doc) {
  if (!doc) return;

  const drawerInner = document.querySelector('.drawer__inner.mini-cart-content');
  if (!drawerInner) return;

  const progressFromDoc = doc.querySelector('.cart-progress-bar-sec');
  let progressEl = drawerInner.querySelector('.cart-progress-bar-sec');

  if (progressFromDoc) {
    const clone = progressFromDoc.cloneNode(true);
    if (progressEl) {
      progressEl.replaceWith(clone);
    } else {
      const header = drawerInner.querySelector('.drawer__header');
      if (header && header.nextSibling) {
        header.parentNode.insertBefore(clone, header.nextSibling);
      } else {
        drawerInner.insertBefore(clone, drawerInner.querySelector('.drawer__panels') || null);
      }
    }
    progressEl = drawerInner.querySelector('.cart-progress-bar-sec');
    if (progressEl) {
      progressEl.hidden = false;
      progressEl.classList.remove('cart-progress-bar-sec--hidden');
    }
    const refresh = () => window.refreshCartProgressBar();
    if (customElements.get('cart-progress-bar')) {
      refresh();
    } else {
      customElements.whenDefined('cart-progress-bar').then(refresh);
    }
  } else if (progressEl) {
    progressEl.remove();
  }
};
